/**
 * Editor geometry on authored shapes: editable nodes, hit testing, bounds and
 * snapping. Coordinates are the spec's `[u, n]` meters.
 */

import type { ObjectPathNode, ObjectPathShape, ObjectShape, Vec2 } from 'shared-types';
import { tessellateObjectShape } from '../../../potree/sketchObjects/custom/evaluateCustomGlyph';
import { roundCoord } from './specOps';

export interface Bounds {
  min: Vec2;
  max: Vec2;
}

export function distanceToSegment(p: Vec2, a: Vec2, b: Vec2): number {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const len2 = dx * dx + dy * dy;
  const t = len2 > 0 ? Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / len2)) : 0;
  return Math.hypot(p[0] - (a[0] + t * dx), p[1] - (a[1] + t * dy));
}

/** Distance from `p` to the shape's outline. */
export function distanceToShape(shape: ObjectShape, p: Vec2): number {
  let best = Infinity;
  for (const poly of tessellateObjectShape(shape)) {
    for (let i = 0; i + 1 < poly.points.length; i++) best = Math.min(best, distanceToSegment(p, poly.points[i], poly.points[i + 1]));
  }
  return best;
}

export function shapeBounds(shape: ObjectShape): Bounds {
  let minU = Infinity;
  let minN = Infinity;
  let maxU = -Infinity;
  let maxN = -Infinity;
  for (const poly of tessellateObjectShape(shape)) {
    for (const [u, n] of poly.points) {
      minU = Math.min(minU, u);
      minN = Math.min(minN, n);
      maxU = Math.max(maxU, u);
      maxN = Math.max(maxN, n);
    }
  }
  return Number.isFinite(minU) ? { min: [minU, minN], max: [maxU, maxN] } : { min: [0, 0], max: [0, 0] };
}

export function unionBounds(list: readonly Bounds[]): Bounds | null {
  if (list.length === 0) return null;
  return {
    min: [Math.min(...list.map((b) => b.min[0])), Math.min(...list.map((b) => b.min[1]))],
    max: [Math.max(...list.map((b) => b.max[0])), Math.max(...list.map((b) => b.max[1]))],
  };
}

export function boundsIntersect(a: Bounds, b: Bounds): boolean {
  return a.min[0] <= b.max[0] && a.max[0] >= b.min[0] && a.min[1] <= b.max[1] && a.max[1] >= b.min[1];
}

export function boundsContain(outer: Bounds, inner: Bounds): boolean {
  return inner.min[0] >= outer.min[0] && inner.max[0] <= outer.max[0] && inner.min[1] >= outer.min[1] && inner.max[1] <= outer.max[1];
}

// ─── Editable nodes ─────────────────────────────────────────────────────

/**
 * The draggable points of a shape: polyline vertices; rect corners
 * (min, (max,min), max, (min,max)); circle center + a radius point on +u;
 * arc center + start + end; path anchors (their bezier handles are
 * separate, see {@link pathHandles}).
 */
export function shapeNodes(shape: ObjectShape): Vec2[] {
  switch (shape.kind) {
    case 'polyline':
      return [...shape.points];
    case 'rect':
      return [shape.min, [shape.max[0], shape.min[1]], shape.max, [shape.min[0], shape.max[1]]];
    case 'circle':
      return [shape.center, [shape.center[0] + shape.radius, shape.center[1]]];
    case 'arc':
      return [
        shape.center,
        [shape.center[0] + shape.radius * Math.cos(shape.startAngle), shape.center[1] + shape.radius * Math.sin(shape.startAngle)],
        [shape.center[0] + shape.radius * Math.cos(shape.endAngle), shape.center[1] + shape.radius * Math.sin(shape.endAngle)],
      ];
    case 'path':
      return shape.nodes.map((n) => n.point);
  }
}

const r = (p: Vec2): Vec2 => [roundCoord(p[0]), roundCoord(p[1])];

/** The shape with node `index` moved to `p` (see {@link shapeNodes} for the node order). */
export function moveShapeNode(shape: ObjectShape, index: number, p: Vec2): ObjectShape {
  switch (shape.kind) {
    case 'polyline':
      return { ...shape, points: shape.points.map((q, i) => (i === index ? r(p) : q)) };
    case 'rect': {
      // The dragged corner moves; the opposite one stays.
      const nodes = shapeNodes(shape);
      const opposite = nodes[(index + 2) % 4];
      return { ...shape, min: r([Math.min(p[0], opposite[0]), Math.min(p[1], opposite[1])]), max: r([Math.max(p[0], opposite[0]), Math.max(p[1], opposite[1])]) };
    }
    case 'circle':
      if (index === 0) return { ...shape, center: r(p) };
      return { ...shape, radius: roundCoord(Math.hypot(p[0] - shape.center[0], p[1] - shape.center[1])) };
    case 'arc': {
      if (index === 0) return { ...shape, center: r(p) };
      const angle = Math.atan2(p[1] - shape.center[1], p[0] - shape.center[0]);
      const radius = roundCoord(Math.hypot(p[0] - shape.center[0], p[1] - shape.center[1]));
      return index === 1 ? { ...shape, radius, startAngle: angle } : { ...shape, radius, endAngle: angle };
    }
    case 'path': {
      // An anchor carries its handles along.
      const node = shape.nodes[index];
      if (!node) return shape;
      const du = p[0] - node.point[0];
      const dn = p[1] - node.point[1];
      const shift = (q: Vec2): Vec2 => r([q[0] + du, q[1] + dn]);
      return { ...shape, nodes: shape.nodes.map((n, i) => (i === index ? { point: r(p), ...(n.in ? { in: shift(n.in) } : {}), ...(n.out ? { out: shift(n.out) } : {}) } : n)) };
    }
  }
}

// ─── Bezier paths (pen tool) ────────────────────────────────────────────

export type PathHandleSide = 'in' | 'out';

/** Every bezier handle of a path, with its anchor (for drawing the handle line). */
export function pathHandles(shape: ObjectPathShape): Array<{ index: number; side: PathHandleSide; point: Vec2; anchor: Vec2 }> {
  return shape.nodes.flatMap((n, index) => [
    ...(n.in ? [{ index, side: 'in' as const, point: n.in, anchor: n.point }] : []),
    ...(n.out ? [{ index, side: 'out' as const, point: n.out, anchor: n.point }] : []),
  ]);
}

const mirror = (anchor: Vec2, q: Vec2): Vec2 => r([2 * anchor[0] - q[0], 2 * anchor[1] - q[1]]);

/** Whether a node's two handles are collinear through it (a smooth node: dragging one turns the other). */
export function isSmoothNode(node: ObjectPathNode): boolean {
  if (!node.in || !node.out) return false;
  const a: Vec2 = [node.in[0] - node.point[0], node.in[1] - node.point[1]];
  const b: Vec2 = [node.out[0] - node.point[0], node.out[1] - node.point[1]];
  const la = Math.hypot(a[0], a[1]);
  const lb = Math.hypot(b[0], b[1]);
  if (la < 1e-9 || lb < 1e-9) return false;
  return (a[0] * b[0] + a[1] * b[1]) / (la * lb) < -0.999;
}

/**
 * Move one handle to `p`. On a smooth node the opposite handle turns along
 * (keeping its own length), unless `breakHandles` (Alt) makes it a cusp.
 */
export function movePathHandle(shape: ObjectPathShape, index: number, side: PathHandleSide, p: Vec2, breakHandles: boolean): ObjectPathShape {
  return {
    ...shape,
    nodes: shape.nodes.map((n, i) => {
      if (i !== index) return n;
      const other: PathHandleSide = side === 'in' ? 'out' : 'in';
      const otherPoint = n[other];
      const next: ObjectPathNode = { ...n, [side]: r(p) };
      if (breakHandles || !otherPoint || !isSmoothNode(n)) return next;
      const dx = n.point[0] - p[0];
      const dy = n.point[1] - p[1];
      const len = Math.hypot(dx, dy);
      if (len < 1e-9) return next;
      const keep = Math.hypot(otherPoint[0] - n.point[0], otherPoint[1] - n.point[1]);
      return { ...next, [other]: r([n.point[0] + (dx / len) * keep, n.point[1] + (dy / len) * keep]) };
    }),
  };
}

/**
 * Toggle a node between a sharp corner (no handles) and a smooth node whose
 * handles run parallel to its neighbours, a third of the way to each.
 */
export function togglePathNodeSmooth(shape: ObjectPathShape, index: number): ObjectPathShape {
  const node = shape.nodes[index];
  if (!node) return shape;
  if (node.in || node.out) return { ...shape, nodes: shape.nodes.map((n, i) => (i === index ? { point: n.point } : n)) };
  const count = shape.nodes.length;
  const prev = index > 0 ? shape.nodes[index - 1] : shape.closed ? shape.nodes[count - 1] : null;
  const next = index < count - 1 ? shape.nodes[index + 1] : shape.closed ? shape.nodes[0] : null;
  const p = node.point;
  const a = prev?.point ?? mirror(p, next?.point ?? [p[0] + 0.1, p[1]]);
  const b = next?.point ?? mirror(p, a);
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const len = Math.hypot(dx, dy);
  if (len < 1e-9) return shape;
  const ux = dx / len;
  const uy = dy / len;
  const inLen = Math.hypot(p[0] - a[0], p[1] - a[1]) / 3;
  const outLen = Math.hypot(b[0] - p[0], b[1] - p[1]) / 3;
  const smooth: ObjectPathNode = { point: p, in: r([p[0] - ux * inLen, p[1] - uy * inLen]), out: r([p[0] + ux * outLen, p[1] + uy * outLen]) };
  return { ...shape, nodes: shape.nodes.map((n, i) => (i === index ? smooth : n)) };
}

/** A new smooth pen node at `point` whose out handle is `drag` (the in handle mirrors it); no drag = a corner. */
export function penNode(point: Vec2, drag: Vec2 | null, cusp: boolean, previous?: ObjectPathNode): ObjectPathNode {
  if (!drag || Math.hypot(drag[0] - point[0], drag[1] - point[1]) < 1e-6) return previous?.in ? { point, in: previous.in } : { point };
  if (cusp) return { point, ...(previous?.in ? { in: previous.in } : {}), out: r(drag) };
  return { point, in: mirror(point, drag), out: r(drag) };
}

// ─── Snapping ───────────────────────────────────────────────────────────

export interface SnapOptions {
  /** Grid step in meters (0 = off). */
  grid: number;
  /** A point the grid runs through (default the origin). */
  gridOrigin?: Vec2;
  /** Candidate points (vertices, footprint corners/midpoints) snapped to within `tolerance`. */
  points: readonly Vec2[];
  tolerance: number;
}

export interface SnapResult {
  point: Vec2;
  /** The snapped-to point, when a point (not the grid) won — the canvas marks it. */
  target: Vec2 | null;
}

export function snapPoint(p: Vec2, { grid, gridOrigin = [0, 0], points, tolerance }: SnapOptions): SnapResult {
  let best: Vec2 | null = null;
  let bestDistance = tolerance;
  for (const q of points) {
    const d = Math.hypot(p[0] - q[0], p[1] - q[1]);
    if (d <= bestDistance) {
      best = q;
      bestDistance = d;
    }
  }
  if (best) return { point: best, target: best };
  if (grid > 0) {
    const [ou, on] = gridOrigin;
    return { point: [roundCoord(ou + Math.round((p[0] - ou) / grid) * grid), roundCoord(on + Math.round((p[1] - on) / grid) * grid)], target: null };
  }
  return { point: r(p), target: null };
}

/** Constrain `p` so the segment from `from` runs at a multiple of 45° (Shift while drawing). */
export function constrainAngle(from: Vec2, p: Vec2): Vec2 {
  const dx = p[0] - from[0];
  const dy = p[1] - from[1];
  const len = Math.hypot(dx, dy);
  if (len === 0) return p;
  const step = Math.PI / 4;
  const angle = Math.round(Math.atan2(dy, dx) / step) * step;
  return r([from[0] + Math.cos(angle) * len, from[1] + Math.sin(angle) * len]);
}

/** Snap candidates: every node of every shape plus the footprint's corners, edge midpoints and center. */
export function snapCandidates(shapes: readonly ObjectShape[], width: number, depth: number, exclude: ReadonlySet<string> = new Set(), includeFootprint = true): Vec2[] {
  const hw = width / 2;
  const hd = depth / 2;
  const footprint: Vec2[] = [
    [-hw, -hd],
    [hw, -hd],
    [hw, hd],
    [-hw, hd],
    [0, -hd],
    [0, hd],
    [-hw, 0],
    [hw, 0],
    [0, 0],
  ];
  return [...(includeFootprint ? footprint : []), ...shapes.filter((s) => !exclude.has(s.id)).flatMap(shapeNodes)];
}
